import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http } from "./api";
import { useAuth } from "./AuthContext";

const WS = createContext(null);

export function WorkspaceProvider({ children }) {
  const { status } = useAuth();
  const [universities, setUniversities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [emails, setEmails] = useState([]);
  const [profile, setProfile] = useState(null);
  const [completeness, setCompleteness] = useState({ percent: 0, missing_core: [], core_filled: 0, core_total: 10 });
  const [gmail, setGmail] = useState({ connected: false, email: null });
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (status !== "authed") return;
    try {
      const [u, t, s, e, p, g] = await Promise.all([
        http.get("/universities").then((r) => r.data),
        http.get("/tasks").then((r) => r.data),
        http.get("/scholarships").then((r) => r.data),
        http.get("/emails").then((r) => r.data),
        http.get("/profile").then((r) => r.data),
        http.get("/gmail/status").then((r) => r.data).catch(() => ({ connected: false })),
      ]);
      setUniversities(u);
      setTasks(t);
      setScholarships(s);
      setEmails(e);
      setProfile(p.profile || {});
      setCompleteness(p.completeness || { percent: 0, missing_core: [] });
      setGmail(g);
      setLoaded(true);
    } catch (err) {
      console.error("workspace load error", err);
    }
  }, [status]);

  useEffect(() => { refresh(); }, [refresh]);

  // ---- universities ----
  const addUniversity = async (data) => {
    const { data: created } = await http.post("/universities", data);
    setUniversities((u) => [...u, created]);
    return created;
  };
  const updateUniversity = async (id, data) => {
    const { data: updated } = await http.patch(`/universities/${id}`, data);
    setUniversities((u) => u.map((x) => (x.id === id ? updated : x)));
    return updated;
  };
  const deleteUniversity = async (id) => {
    await http.delete(`/universities/${id}`);
    setUniversities((u) => u.filter((x) => x.id !== id));
  };
  // ---- documents (per-university uploads) ----
  const backfillUniversity = async (id) => {
    setUniversities((u) => u.map((x) => (x.id === id ? { ...x, research_status: "pending" } : x)));
    const { data } = await http.post(`/universities/${id}/backfill-research`);
    if (data?.data) {
      setUniversities((u) => u.map((x) => (x.id === id ? data.data : x)));
    }
    return data;
  };
  const computeCostAndAid = async (id) => {
    const { data } = await http.post(`/universities/${id}/cost-and-aid`);
    if (data?.data) {
      setUniversities((u) => u.map((x) => (x.id === id ? { ...x, cost_aid: data.data } : x)));
    }
    return data;
  };
  const syncChecklistTasks = async (id) => {
    const { data } = await http.post(`/universities/${id}/sync-checklist-tasks`);
    // Refresh tasks so the Task Center reflects newly synced items.
    try {
      const { data: t } = await http.get("/tasks");
      setTasks(t);
    } catch (err) {
      console.warn("[sync-tasks] task refresh failed:", err);
    }
    return data;
  };

  const listDocuments = async (uid) => {
    const { data } = await http.get(`/universities/${uid}/documents`);
    return data;
  };
  const uploadDocument = async (uid, file, { name, doc_type } = {}) => {
    const form = new FormData();
    form.append("file", file);
    if (name) form.append("name", name);
    if (doc_type) form.append("doc_type", doc_type);
    const { data } = await http.post(`/universities/${uid}/documents`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };
  const renameDocument = async (docId, patch) => {
    const { data } = await http.patch(`/documents/${docId}`, patch);
    return data;
  };
  const deleteDocument = async (docId) => {
    await http.delete(`/documents/${docId}`);
  };
  const duplicateDocument = async (docId, targetUniId, name) => {
    const { data } = await http.post(`/documents/${docId}/duplicate`, {
      target_uni_id: targetUniId,
      ...(name ? { name } : {}),
    });
    return data;
  };

  // ---- tasks ----
  const addTask = async (data) => {
    const { data: created } = await http.post("/tasks", data);
    setTasks((t) => [...t, created]);
    return created;
  };
  const updateTask = async (id, data) => {
    const { data: updated } = await http.patch(`/tasks/${id}`, data);
    setTasks((t) => t.map((x) => (x.id === id ? updated : x)));
    return updated;
  };
  const deleteTask = async (id) => {
    await http.delete(`/tasks/${id}`);
    setTasks((t) => t.filter((x) => x.id !== id));
  };

  // ---- scholarships ----
  const addScholarship = async (data) => {
    const { data: created } = await http.post("/scholarships", data);
    setScholarships((s) => [...s, created]);
    return created;
  };
  const updateScholarship = async (id, data) => {
    const { data: updated } = await http.patch(`/scholarships/${id}`, data);
    setScholarships((s) => s.map((x) => (x.id === id ? updated : x)));
    return updated;
  };
  const deleteScholarship = async (id) => {
    await http.delete(`/scholarships/${id}`);
    setScholarships((s) => s.filter((x) => x.id !== id));
  };

  // ---- emails ----
  const addEmail = async (data) => {
    const { data: created } = await http.post("/emails", data);
    setEmails((e) => [...e, created]);
    return created;
  };

  // ---- profile ----
  const updateProfile = async (patch) => {
    const { data } = await http.put("/profile", patch);
    setProfile(data.profile || {});
    setCompleteness(data.completeness || completeness);
    return data;
  };

  // ---- gmail ----
  const refreshGmailStatus = async () => {
    try {
      const { data } = await http.get("/gmail/status");
      setGmail(data);
      return data;
    } catch (e) {
      return null;
    }
  };
  const connectGmail = async () => {
    const { data } = await http.post("/gmail/connect");
    return data;
  };
  const disconnectGmail = async () => {
    await http.delete("/gmail/disconnect");
    setGmail({ connected: false, email: null });
  };
  const draftToGmail = async ({ to, subject, body, university_id, professor }) => {
    const { data } = await http.post("/gmail/draft", { to, subject, body, university_id, professor });
    if (data.email) setEmails((prev) => [data.email, ...prev]);
    return data;
  };
  const sendViaGmail = async (payload) => {
    const { data } = await http.post("/gmail/send", payload);
    await refresh();
    return data;
  };
  const syncGmailEmail = async (email_id) => {
    const { data } = await http.post(`/gmail/sync/${email_id}`);
    await refresh();
    return data;
  };
  const syncAllGmail = async () => {
    const { data } = await http.post("/gmail/sync-all");
    await refresh();
    return data;
  };

  const value = {
    universities, tasks, scholarships, emails, profile, completeness, gmail, loaded,
    refresh,
    addUniversity, updateUniversity, deleteUniversity, backfillUniversity,
    computeCostAndAid, syncChecklistTasks,
    listDocuments, uploadDocument, renameDocument, deleteDocument, duplicateDocument,
    addTask, updateTask, deleteTask,
    addScholarship, updateScholarship, deleteScholarship,
    addEmail,
    updateProfile,
    refreshGmailStatus, connectGmail, disconnectGmail,
    draftToGmail, sendViaGmail, syncGmailEmail, syncAllGmail,
  };

  return <WS.Provider value={value}>{children}</WS.Provider>;
}

export const useWorkspace = () => useContext(WS);
